import { useCallback, useContext } from "react";
import { AuthContext } from "../context/auth-context";
import { useHttpClient } from "./http-hook";

const adsBase =
  process.env.REACT_APP_ADS ||
  (process.env.REACT_APP_JOBS
    ? process.env.REACT_APP_JOBS.replace(/\/api\/jobs\/?$/, "/api/ads")
    : "");

// Keep in sync with backend lib/ad-pricing.js.
// CPC-only: advertisers pay per click, no upfront day rate.
export const AD_CREDITS_PER_CLICK = 0.1;

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

const round2 = (n) => Math.round(n * 100) / 100;

// Available ad slots across the site. The `value` is the identifier both
// the picker (form) and <AdPlacement placement="..." /> (display) use.
export const AD_PLACEMENTS = [
  { value: "home", label: "Home" },
  { value: "jobs", label: "Jobs" },
  { value: "content", label: "Content" },
  { value: "income-directory", label: "Income Directory" },
  { value: "teacher-dashboard", label: "Teacher Dashboard" },
];

// Calendar-day span (inclusive). Same day = 1. End before start = 0.
// Inclusive day span when both dates are set. null = open-ended (no end date).
// 0 = end is before start (invalid).
export const getAdDurationDays = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const diff = end.getTime() - start.getTime();
  if (diff < 0) return 0;
  return Math.floor(diff / MS_PER_DAY) + 1;
};

// Estimated spend at the CPC rate for a given click count.
export const getEstimatedAdCost = (estimatedClicks = 0) =>
  round2(estimatedClicks * AD_CREDITS_PER_CLICK);

// Ensure links are absolute so <a href> and window.open behave correctly.
export const normalizeExternalLink = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

// Hostname only (no path/query), www stripped. e.g. bettersaid.com, site.co.uk
export const getAdDisplayDomain = (url) => {
  const normalized = normalizeExternalLink(url);
  if (!normalized) return "";
  try {
    return new URL(normalized).hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
};

// Builds multipart form data. `imageFile` (File) wins over `image` (URL).
const buildAdFormData = (adData) => {
  const formData = new FormData();
  formData.append("mainTitle", adData?.mainTitle || "");
  formData.append("subText", adData?.subText || "");
  formData.append("externalLink", adData?.externalLink || "");
  formData.append("startDate", adData?.startDate || "");
  // Empty / missing endDate is open-ended; backend stores null
  if (adData?.endDate) {
    formData.append("endDate", adData.endDate);
  } else {
    formData.append("endDate", "");
  }
  formData.append("placements", JSON.stringify(adData?.placements || []));
  if (adData?.imageFile) {
    formData.append("image", adData.imageFile);
  } else if (adData?.image) {
    formData.append("image", adData.image);
  }
  return formData;
};

export const useAds = () => {
  const auth = useContext(AuthContext);
  const { isLoading, isPostLoading, sendRequest, error, clearError } =
    useHttpClient();

  // POST - create an ad listing (employers only, goes to review).
  // Sent as multipart so an image file OR an image url both work.
  const createAd = useCallback(
    async (userId, adData) => {
      if (!userId || !auth?.token) return null;
      const response = await sendRequest(
        `${adsBase}/create-ad/${userId}`,
        "POST",
        buildAdFormData(adData),
        // no Content-Type: the browser sets the multipart boundary
        { Authorization: "Bearer " + auth.token }
      );
      return response?.ad || null;
    },
    [auth?.token, sendRequest]
  );

  // PATCH - update an ad listing. Backend resets it to "pending" review.
  const updateAd = useCallback(
    async (adId, adData) => {
      if (!adId || !auth?.token) return null;
      const response = await sendRequest(
        `${adsBase}/${adId}`,
        "PATCH",
        buildAdFormData(adData),
        { Authorization: "Bearer " + auth.token }
      );
      return response?.ad || null;
    },
    [auth?.token, sendRequest]
  );

  // DELETE - remove an ad listing (pending ads are refunded)
  const deleteAd = useCallback(
    async (adId) => {
      if (!adId || !auth?.token) return null;
      const response = await sendRequest(`${adsBase}/${adId}`, "DELETE", null, {
        Authorization: "Bearer " + auth.token,
      });
      return response || null;
    },
    [auth?.token, sendRequest]
  );

  // GET - the logged-in user's own ad listings
  const getMyAds = useCallback(
    async (userId) => {
      if (!userId || !auth?.token) return [];
      const response = await sendRequest(`${adsBase}/my-ads/${userId}`, "GET", null, {
        Authorization: "Bearer " + auth.token,
      });
      return response?.ads || [];
    },
    [auth?.token, sendRequest]
  );

  // GET - public active ads, optionally by placement
  const getActiveAds = useCallback(
    async (placement) => {
      const query = placement ? `?placement=${encodeURIComponent(placement)}` : "";
      const response = await sendRequest(`${adsBase}/${query}`);
      return response?.ads || [];
    },
    [sendRequest]
  );

  // PATCH - public click tracking (fire-and-forget)
  const trackAdClick = useCallback(
    async (adId) => {
      if (!adId) return;
      try {
        await sendRequest(`${adsBase}/click/${adId}`, "PATCH", null, {});
      } catch (err) {
        // tracking should never block the redirect
        console.log(err);
      }
    },
    [sendRequest]
  );

  // PATCH - public impression tracking, batched by adIds (fire-and-forget)
  const trackAdImpressions = useCallback(
    async (adIds) => {
      if (!Array.isArray(adIds) || adIds.length === 0) return;
      try {
        await sendRequest(
          `${adsBase}/impressions`,
          "PATCH",
          JSON.stringify({ adIds }),
          { "Content-Type": "application/json" }
        );
      } catch (err) {
        console.log(err);
      }
    },
    [sendRequest]
  );

  return {
    isLoading,
    isPostLoading,
    error,
    clearError,
    createAd,
    updateAd,
    deleteAd,
    getMyAds,
    getActiveAds,
    trackAdClick,
    trackAdImpressions,
    adsBase,
  };
};
